import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { CreditCard, CheckCircle2, DollarSign } from "lucide-react";

const BTN_STYLE = {
  background: "#14243D",
  color: "#ffffff",
};

export default function PagamentoCard({ orc, token, isAdmin }) {
  const [info, setInfo] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/orcamento/${token}/pagamento`);
      setInfo(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token, orc?.status, orc?.valor_total]);

  if (!info) return null;

  const valorTotal = info.valor_total || 0;
  const totalPago = info.total_pago || 0;
  const saldo = info.saldo || 0;
  const pct = valorTotal > 0 ? Math.min(100, Math.round((totalPago / valorTotal) * 100)) : 0;

  // Pago integral
  if (info.status === "pago") {
    return (
      <div
        className="rounded-lg border p-5 text-emerald-900"
        style={{ background: "#ECFDF5", borderColor: "#A7F3D0" }}
        data-testid="pagamento-pago-feedback"
      >
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 className="w-5 h-5" />
          <div className="font-heading text-lg font-bold">Orçamento quitado</div>
        </div>
        <div className="text-sm">
          Total pago: <strong>{money(totalPago)}</strong>
          {info.pagamentos?.length > 1 ? ` em ${info.pagamentos.length} pagamento(s)` : ""}
        </div>
      </div>
    );
  }

  if (!info.liberado) {
    // Orçamento ainda não aprovado — não mostra nada pro cliente
    if (!isAdmin) return null;
    return (
      <div className="rounded-md border p-3 text-xs" style={{ background: "#FFFBEB", borderColor: "#FDE68A", color: "#92400E" }} data-testid="aviso-aguardando-aprovacao">
        <strong>Aguardando aprovação do cliente.</strong> O botão de pagamento aparece aqui automaticamente após a aprovação.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="pagamento-bloco">
      {/* Progresso parcial */}
      {info.status === "parcial" && (
        <div className="rounded-lg border p-4" style={{ background: "#F0FDF4", borderColor: "#BBF7D0" }} data-testid="pagamento-progresso">
          <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
            <div className="text-sm text-emerald-900">
              Pago <strong>{money(totalPago)}</strong> de {money(valorTotal)}
            </div>
            <div className="text-xs text-emerald-800">Falta <strong>{money(saldo)}</strong></div>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "#DCFCE7" }}>
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: "#16A34A" }} />
          </div>
        </div>
      )}

      {/* Botão de pagamento — inline style para evitar conflito com .bbh-card */}
      <Link
        to={`/pagamento/${token}`}
        data-testid="ir-para-pagamento-btn"
        className="block rounded-lg p-5 shadow-md hover:brightness-110 transition no-underline"
        style={BTN_STYLE}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-heading text-xl font-bold leading-tight" style={{ color: "#ffffff" }}>
              {info.status === "parcial" ? "Pagar restante" : "Pagar orçamento"}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.85)" }}>
              {info.status === "parcial"
                ? <>Saldo devedor <strong>{money(saldo)}</strong> — via PIX</>
                : <>PIX à vista — total {money(valorTotal)} · cartão em implantação</>}
              {isAdmin ? " · (visualização admin)" : ""}
            </div>
          </div>
          <div className="shrink-0 rounded-full p-2.5" style={{ background: "rgba(255,255,255,0.12)" }}>
            <CreditCard className="w-6 h-6" style={{ color: "#ffffff" }} />
          </div>
        </div>
      </Link>

      {/* Histórico de pagamentos */}
      {info.pagamentos?.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="pagamento-historico">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">
            Pagamentos já realizados
          </div>
          <ul className="space-y-1.5 text-sm">
            {info.pagamentos.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-zinc-700">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
                  {money(p.valor)} <span className="text-zinc-500 text-xs">· {p.metodo === "pix" ? "PIX" : `Cartão ${p.parcelas}x`}</span>
                </span>
                <span className="text-xs text-zinc-500">
                  {p.data ? new Date(p.data).toLocaleDateString("pt-BR") : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
